<template>
  <div class="flex justify-center items-center h-full" ref="gridWrapper">
    <ul
      role="list"
      class="grid-container w-full mx-auto mt-24 h-full grid gap-2 relative"
      :class="gridClasses"
      :style="fillContainerKeepingAspectRatioCss"
    >
      <li
        class="relative rounded-2xl border border-gray-600"
        :class="
          sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION
            ? 'col-span-1 row-span-1'
            : 'col-span-2 row-span-2'
        "
      >
        <div
          class="h-full w-full group rounded-2xl block overflow-hidden relative"
          v-show="!sessionStore.mediaPermissionError"
        >
          <StreamPlayerLocal
            :statsValue="localConnectionQuality"
            :isMinimizable="false"
            :showTitle="true"
            class="rounded camera"
          />
        </div>
        <SpeakingIndicator :audioLevel="localAudioLevel" />
      </li>

      <template v-if="screenShareStream.length">
        <ScreenShareTile
          v-for="(screenshareProps, index) in screenShareStream"
          :index="index"
          :gridDimension="gridDimension"
          :key="screenshareProps.jitsiUserId"
          :userJitsiId="screenshareProps?.jitsiUserId"
          :remoteDisplayInfo="screenshareProps"
          :setVideoAudioRefs="setVideoAudioRefs"
          :shouldShowRemoteUserVideo="shouldShowRemoteUserVideo"
          :fillContainerKeepingAspectRatioCss="fillContainerKeepingAspectRatioCss"
        />
      </template>
      <template v-if="participantsRemoteStreams.length">
        <RemoteUsersTile
          v-for="(props, index) in participantsRemoteStreams"
          :index="index"
          :gridDimension="gridDimension"
          :key="props.jitsiUserId"
          :userJitsiId="props.jitsiUserId"
          :isClinicianStream="false"
          :remoteDisplayInfo="props"
          :setVideoAudioRefs="setVideoAudioRefs"
          :shouldShowRemoteUserVideo="shouldShowRemoteUserVideo"
          :moderatorId="moderatorId"
          :vuzixStreamId="vuzixStreamId"
        />
      </template>
      <SessionPausedNotification
        v-if="sessionStore.isPhasePaused"
        :currentPhase="sessionStore.currentPhaseData.title"
        :isClinician="true"
      />
    </ul>
  </div>
</template>

<script>
import { ref, reactive, onUnmounted, onMounted, watch, computed } from 'vue';
import { VICE_SESSION_PHASES, CAMERA_TYPES } from '@/Utils/vice-constants';
import StreamPlayerLocal from '@/Components/Session/Grid/StreamPlayerLocal.vue';
import useSessionStore from '@/Stores/useSessionStore';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import SessionPausedNotification from '@/Components/Session/Overlays/SessionPausedOverlay';
import SpeakingIndicator from '@/Components/Session/Grid/SpeakingIndicator';
import useGroupCallHandlers from '@/Composables/useGroupCallHandlers';
import JitsiService from '@/Services/JitsiService';
import RemoteUsersTile from '@/Components/Session/Grid/RemoteUsersTile.vue';
import ScreenShareTile from '@/Components/Session/Grid/ScreenShareTile.vue';

export default {
  components: {
    StreamPlayerLocal,
    SessionPausedNotification,
    SpeakingIndicator,
    RemoteUsersTile,
    ScreenShareTile,
  },
  setup() {
    const sessionStore = useSessionStore();
    const remoteUserProps = reactive({});
    const videoAudioRefs = reactive({});
    const moderatorId = ref(null);
    const vuzixStreamId = ref(null);
    const isJoined = ref(false);
    const myJitsiId = ref(null);
    const jitsiRoom = ref(null);
    const disconnect = ref(null);
    const smartphoneStreamId = ref(null);
    const localAudioLevel = ref(0);
    const localConnectionQuality = ref(0);
    const gridDimension = Array(20).fill({ cols: 1, rows: 1, start: 1 });

    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const internalCameraUser = computed(
      () => multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM],
    );

    const screenShareStream = computed(() =>
      _.filter(remoteUserProps, (_props) => /ScreenshareUser/.test(_props.userId)),
    );

    const participantsRemoteStreams = computed(() =>
      _.filter(
        remoteUserProps,
        (_props) =>
          _props.jitsiUserId !== moderatorId.value &&
          !/ScreenshareUser/.test(_props.userId) &&
          _props.jitsiUserId !== vuzixStreamId.value,
      ),
    );

    /**
     * When a user joins a conference, add the media track to the conference/room
     */
    const onConferenceJoined = async () => {
      const localTracks = internalCameraUser.value?.localTracks;
      const conferenceAudioTrack = internalCameraUser.value.jitsiRoom.getLocalAudioTrack();
      if (localTracks?.length) {
        isJoined.value = true;
        for (const newTrack of localTracks) {
          // Replace audio track if already exists
          if (conferenceAudioTrack === newTrack || conferenceAudioTrack?.type === newTrack?.type) {
            // eslint-disable-next-line no-await-in-loop
            await internalCameraUser.value.jitsiRoom?.replaceTrack(conferenceAudioTrack, newTrack);
          } else {
            // eslint-disable-next-line no-await-in-loop
            await internalCameraUser.value.jitsiRoom?.addTrack(newTrack);
          }
        }
      }
    };

    const resetMicState = async (shouldTurnOnMic) => {
      await multiCameraDevicesStore.resetMicState(shouldTurnOnMic);
    };

    const { events, commands } = useGroupCallHandlers({
      remoteUserProps,
      videoAudioRefs,
      room: jitsiRoom,
      onConferenceJoined,
      vuzixStreamId,
      moderatorId,
      localAudioLevel,
      localConnectionQuality,
      myJitsiId,
      resetMicState,
      smartphoneStreamId,
    });

    const checkIfAudioAndVideoTracksExist = () => {
      const trackTypes = [];
      internalCameraUser.value?.localTracks?.forEach((track) => {
        trackTypes.push(track.type);
      });

      return trackTypes.includes('audio') && trackTypes.includes('video');
    };

    const isReady = computed(() => jitsiRoom.value && checkIfAudioAndVideoTracksExist());

    watch(isReady, (isReadyToJoin) => {
      if (isReadyToJoin) {
        initializeRoom();
      }
    });

    onUnmounted(async () => {
      try {
        await disconnect.value();
      } catch (error) {
        console.error(error);
      }
    });

    onMounted(async () => {
      // Disconnect External devices if present on introduction/debrief
      if (sessionStore.currentPhase !== VICE_SESSION_PHASES.CONSULTATION) {
        await multiCameraDevicesStore.handleExternalDevicesDisconnect();
        await multiCameraDevicesStore.removeCamera(CAMERA_TYPES.EXTERNAL_WEBCAM, false);
      }

      const { room, disconnect: disconnectJitsi } = await JitsiService().makeConnection(
        sessionStore.sessionId,
        events,
        commands,
      );
      jitsiRoom.value = room;
      disconnect.value = disconnectJitsi;
      multiCameraDevicesStore.setActiveJitsiRoom(CAMERA_TYPES.INTERNAL_WEBCAM, room);
      new ResizeObserver(calculateFillContainerKeepingAspectRatioCss).observe(gridWrapper.value);
    });

    const setVideoAudioRefs = (el, id) => {
      if (!videoAudioRefs[id]) {
        videoAudioRefs[id] = [];
      }
      if (el) {
        videoAudioRefs[id].push(el);
      }
    };

    const initializeRoom = () => {
      if (internalCameraUser.value?.localTracks) {
        jitsiRoom.value?.setDisplayName(
          `${sessionStore.authUser?.name}-Clinician-${sessionStore.authUser.id}`,
        );
        jitsiRoom.value?.setLocalParticipantProperty('isClinician', true);
        const profilePhoto = sessionStore.authUser?.profile_photo_url
          ? sessionStore.authUser.static_avatar_url
          : null;
        jitsiRoom.value?.setLocalParticipantProperty('avatar', profilePhoto);
        jitsiRoom.value?.join();
        myJitsiId.value = jitsiRoom.value?.myUserId();
      }
    };

    // Ensure disconnection where there's a refresh.
    // window.onbeforeunload = disconnect;

    const gridClasses = computed(() => {
      let totalGridBoxes = Object.keys(remoteUserProps).length;
      totalGridBoxes = moderatorId.value ? totalGridBoxes : totalGridBoxes + 1;
      let classes = ' grid-rows-1 grid-cols-1';
      if (totalGridBoxes >= 1) classes = ' grid-rows-3 grid-cols-3';
      if (totalGridBoxes > 5) classes = ' grid-rows-4 grid-cols-4';
      if (totalGridBoxes > 12) classes = ' grid-rows-5 grid-cols-5';
      return classes;
    });

    // Extract to composable
    const gridWrapper = ref();
    const fillContainerKeepingAspectRatioCss = reactive({ opacity: 0 });

    const calculateFillContainerKeepingAspectRatioCss = () => {
      if (gridWrapper.value) {
        const isPortrait = gridWrapper.value.offsetWidth * 0.5625 < gridWrapper.value.offsetHeight;

        if (isPortrait) {
          fillContainerKeepingAspectRatioCss.maxWidth = `${
            gridWrapper.value.offsetHeight * 1.77777778
          }px`;
          fillContainerKeepingAspectRatioCss.opacity = 1;
          fillContainerKeepingAspectRatioCss.height = 'calc(100vh - 205px)';
        } else {
          fillContainerKeepingAspectRatioCss.maxWidth = `${
            gridWrapper.value.offsetHeight * 1.77777778
          }px`;
          fillContainerKeepingAspectRatioCss.opacity = 1;
          fillContainerKeepingAspectRatioCss.height = 'calc(100vh - 205px)';
        }
      }
    };

    const shouldShowRemoteUserVideo = (participantJitsiId) =>
      remoteUserProps?.[participantJitsiId]?.video &&
      !remoteUserProps?.[participantJitsiId]?.isVideoHidden &&
      !sessionStore.isPutOnHold;

    return {
      myJitsiId,
      gridClasses,
      moderatorId,
      sessionStore,
      gridDimension,
      remoteUserProps,
      setVideoAudioRefs,
      VICE_SESSION_PHASES,
      multiCameraDevicesStore,
      shouldShowRemoteUserVideo,
      fillContainerKeepingAspectRatioCss,
      gridWrapper,
      localAudioLevel,
      screenShareStream,
      vuzixStreamId,
      participantsRemoteStreams,
      localConnectionQuality,
    };
  },
};
</script>

<style lang="scss" scoped>
.grid-container {
  min-height: calc(100vh - 50px);
  opacity: 0;
}

.text-shadow {
  text-shadow: 0 0 2px black;
}

.shadow-filter {
  filter: drop-shadow(0 0 2px black);
}
</style>
